import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchMe, login as apiLogin, logout as apiLogout, type FullProfile } from "../api/auth";
import { tokenStore } from "../api/client";

interface AuthContextValue {
  user: FullProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // Helper : true si l'utilisateur courant est admin.
  isAdmin: boolean;
  // Helper : true si l'utilisateur courant a accès au phone donné.
  // Pour les admins, retourne toujours true. Pour les employees, vérifie
  // l'appartenance à allowed_phone_ids.
  canAccessPhone: (phoneId: string | null | undefined) => boolean;
  // Re-fetch le profil (ex: après une modif user-self).
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<FullProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (tokenStore.get()) {
          const me = await fetchMe();
          if (!cancelled) setUser(me);
        }
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const me = await apiLogin(email, password);
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) return;
    const me = await fetchMe();
    setUser(me);
  }, []);

  const isAdmin = user?.role === "admin";

  const canAccessPhone = useCallback(
    (phoneId: string | null | undefined): boolean => {
      if (!user) return false;
      if (user.role === "admin") return true;
      if (!phoneId) return false;
      return (user.allowed_phone_ids || []).includes(phoneId);
    },
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, refresh, isAdmin, canAccessPhone }),
    [user, loading, login, logout, refresh, isAdmin, canAccessPhone]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return ctx;
}
